import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ImageOff } from 'lucide-react';
import { cn } from '@tappa/ui';
import { fotoLarga } from '../../lib/images.js';
import type { LarghezzaFoto } from '../../lib/images.js';

/**
 * Foto dei luoghi.
 *
 * Le immagini NON sono caricate nell'app: nel database c'e' solo l'indirizzo
 * della foto su Wikimedia Commons, e il browser la scarica da li' al momento in
 * cui apre quella scheda. L'app non ospita, non copia e non ridistribuisce
 * nessun file.
 *
 * Le foto di Commons sono riutilizzabili **a condizione** di citare autore,
 * licenza e fonte: per questo l'attribuzione non e' un dettaglio facoltativo e
 * la scheda del luogo mostra sempre la riga di credito con il link alla pagina
 * originale.
 *
 * Due regole di comportamento:
 *
 *   * se un luogo non ha una foto non se ne mette una generica — una foto
 *     sbagliata e' peggio di nessuna foto, perche' e' proprio dall'immagine che
 *     una persona riconosce il posto;
 *   * se la versione rimpicciolita non si carica si riprova una volta con
 *     l'indirizzo originale, e solo dopo l'immagine sparisce invece di lasciare
 *     l'icona rotta del browser.
 */

export interface PlaceImageProps {
  readonly url: string | null;
  readonly name: string;
  readonly attribution?: string | null;
  readonly pageUrl?: string | null;
}

/**
 * Gestisce il caricamento: prima la versione leggera, poi l'originale come
 * ripiego, poi la resa.
 */
function useFoto(
  url: string | null,
  larghezza: LarghezzaFoto,
): { src: string | null; srcSet: string | null; fallita: () => void } {
  const [ripiego, setRipiego] = useState(false);
  const [fallita, setFallita] = useState(false);

  // Cambiando luogo si riparte dalla versione leggera.
  useEffect(() => {
    setRipiego(false);
    setFallita(false);
  }, [url]);

  if (url === null || fallita) return { src: null, srcSet: null, fallita: () => undefined };

  const src = ripiego ? url : fotoLarga(url, larghezza);

  // Su uno schermo 2x la versione normale si vede sgranata. Il browser sceglie:
  // chi ha un display comune scarica la piccola, gli altri la doppia. Se non
  // esiste una versione piu' grande, `fotoLarga` restituisce la stessa e il
  // `srcSet` viene omesso invece di dichiarare un 2x che 2x non e'.
  const doppia = ripiego ? url : fotoLarga(url, 1280);
  const srcSet = doppia === src ? null : `${src} 1x, ${doppia} 2x`;

  return {
    src,
    srcSet,
    fallita: () => {
      if (ripiego) setFallita(true);
      else setRipiego(true);
    },
  };
}

/** Miniatura per gli elenchi. */
export function PlaceThumb({ url, name, attribution }: PlaceImageProps): ReactNode {
  const { src, fallita } = useFoto(url, 250);

  if (src === null) {
    return (
      <div
        aria-hidden="true"
        className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[var(--r-md)] bg-surface-sunken text-text-subtle"
      >
        <ImageOff size={20} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={`Foto di ${name}`}
      title={attribution ?? undefined}
      loading="lazy"
      decoding="async"
      width={72}
      height={72}
      onError={fallita}
      className="h-[72px] w-[72px] shrink-0 rounded-[var(--r-md)] bg-surface-sunken object-cover"
    />
  );
}

/** Immagine grande per la scheda del luogo, con la riga di credito. */
export function PlaceHero({ url, name, attribution, pageUrl }: PlaceImageProps): ReactNode {
  const { src, srcSet, fallita } = useFoto(url, 500);
  if (src === null) return null;

  return (
    <figure className="mb-3">
      <img
        src={src}
        srcSet={srcSet ?? undefined}
        alt={`Foto di ${name}`}
        loading="lazy"
        decoding="async"
        onError={fallita}
        className="aspect-[16/9] w-full rounded-[var(--r-md)] bg-surface-sunken object-cover"
      />
      {attribution !== null && attribution !== undefined ? (
        <figcaption className="mt-1 text-[12px] leading-4 text-text-subtle">
          {pageUrl !== null && pageUrl !== undefined ? (
            <a href={pageUrl} target="_blank" rel="noreferrer" className="underline">
              {attribution}
            </a>
          ) : (
            attribution
          )}
        </figcaption>
      ) : null}
    </figure>
  );
}

/** Banda compatta usata nella card della prossima tappa. */
export function PlaceBanner({
  url,
  name,
  attribution,
  className,
}: PlaceImageProps & { className?: string }): ReactNode {
  const { src, srcSet, fallita } = useFoto(url, 500);
  if (src === null) return null;

  return (
    <img
      src={src}
      srcSet={srcSet ?? undefined}
      alt={`Foto di ${name}`}
      title={attribution ?? undefined}
      loading="lazy"
      decoding="async"
      onError={fallita}
      className={cn(
        'aspect-[21/9] w-full rounded-[var(--r-md)] bg-surface-sunken object-cover',
        className,
      )}
    />
  );
}
